import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { GitFork, Pencil, PlusIcon, RotateCcw, Trash } from 'lucide-react'
import { Badge, Button, cn, DefaultPopover, DefaultTooltip, InputGroup, InputGroupText, Separator } from 'twyne-new.ui'

import ConfirmationPopover from '@/components/ConfirmationPopover'
import InputSearchList from '@/components/InputSearchList'
import { useBuilderStore } from '@/components/builder/utils/store'
import Form from '@/components/version/Form'
import { useVersionStore, VersionProvider } from '@/components/version/store'

const ENTITY = 'cms_post_builders'
const LIST_PARAMS = { options: { page: 1, sizePerPage: 25 }, autoRefresh: false }
const PANEL_DURATION = 300

export default function Versions() {
  const { postBuilder: { version: { item: current, reset: resetVersion }, history: { preview } }, loading } = useBuilderStore()
  const [open, setOpen] = useState(false)

  const handleDefault = useCallback(async () => {
    setOpen(false)
    await resetVersion()
  }, [resetVersion])

  if (preview) {
    return null
  }

  return <InputGroup button>
    {current && <InputGroupText addonType='prepend'>
      <DefaultTooltip content='Go back to default version' side='top' triggerProps={{ asChild: true }}>
        <Button className='h-8' color='accent' variant='outline' onClick={handleDefault} disabled={loading}>
          <RotateCcw size={16} />
        </Button>
      </DefaultTooltip>
    </InputGroupText>}
    {current && <InputGroupText>
      <DefaultTooltip content='You are editing a custom version' side='top' triggerProps={{ asChild: true }}>
        <Button className='h-8 outline twy-outline-accent' color='accent' variant='light' onClick={() => setOpen(true)}>
          {current.description}
        </Button>
      </DefaultTooltip>
    </InputGroupText>}
    <InputGroupText addonType={current ? 'append' : undefined}>
      <DefaultPopover
        wrapperProps={{ open, onOpenChange: setOpen }}
        triggerProps={{ asChild: true }}
        contentProps={{ align: 'end', className: 'w-auto p-2 flex items-start' }}
        trigger={<Button className='h-8' color='accent' variant='outline'>
          <DefaultTooltip content='Versions' triggerProps={{ asChild: true }}>
            <GitFork size={16} />
          </DefaultTooltip>
        </Button>}
      >
        <VersionProvider>
          <VersionsContent current={current} loading={loading} onClose={() => setOpen(false)} onDefault={handleDefault} />
        </VersionProvider>
      </DefaultPopover>
    </InputGroupText>
  </InputGroup>
}

function VersionsContent({ current, loading, onClose, onDefault }) {
  const { id: entityId } = useParams()
  const { postBuilder: { item: data, deleteEntry, version: { setItem: setVersion } } } = useBuilderStore()
  const { collections: { items: versions, useLoad }, collection: { save: createVersion } } = useVersionStore()

  const [creating, setCreating] = useState(false)
  const [formVisible, setFormVisible] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (creating) {
      setFormVisible(true)
      return undefined
    }

    const timeout = setTimeout(() => setFormVisible(false), PANEL_DURATION)
    return () => clearTimeout(timeout)
  }, [creating])

  const params = useMemo(() => ({ ...LIST_PARAMS, entityId, entity: ENTITY }), [entityId])
  const loadVersions = useLoad(params)

  const term = search.trim().toLowerCase()
  const showDefault = !term || 'default'.includes(term)
  const filtered = useMemo(() => term ? versions.filter(({ description, slug }) => `${description} ${slug || ''}`.toLowerCase().includes(term)) : versions, [versions, term])

  const handleCreate = useCallback(async entry => {
    await createVersion({ ...entry, id: null, entityId, entity: ENTITY })
    await loadVersions()
    setCreating(false)
  }, [createVersion, loadVersions, entityId])

  const handleToggleCreate = useCallback(() => {
    setCreating(creating => {
      if (!creating) {
        setFormKey(key => key + 1)
      }
      return !creating
    })
  }, [])

  const handleEdit = useCallback(async entry => {
    onClose()
    await setVersion(entry)
  }, [onClose, setVersion])

  const handleDelete = useCallback(async entry => {
    const { id, entityId } = entry
    await deleteEntry(entry, { version: { id, entityId } })
    await loadVersions()
  }, [deleteEntry, loadVersions])

  return <>
    <div className='w-76 shrink-0'>
      <div className='flex items-stretch gap-2'>
        <InputSearchList className='flex-1' placeholder='Versions' value={search} onChange={setSearch} />
        <DefaultTooltip content={creating ? 'Close' : 'Create new version'} side='top' triggerProps={{ asChild: true }}>
          <Button size='sm' variant={creating ? 'outline' : 'light'} color='accent' onClick={handleToggleCreate} className='self-stretch px-2.5'>
            <PlusIcon size={14} className={cn('transition-transform duration-300', creating && 'rotate-45')} />
          </Button>
        </DefaultTooltip>
      </div>
      <Separator className='m-0 my-2' />
      <ul className='flex flex-col gap-0.5 max-h-80 overflow-y-auto'>
        {showDefault && <Item
          label='Default'
          isCurrent={!current}
          actions={<Action tooltip='Return to default' icon={RotateCcw} onClick={onDefault} disabled={loading} />}
        />}
        {filtered.map(version => <Item
          key={version.id}
          label={version.description}
          hint={version.slug}
          isCurrent={current?.id === version.id}
          actions={<>
            <Action tooltip='Edit' icon={Pencil} onClick={() => handleEdit(version)} disabled={loading} />
            <DeleteAction version={version} onDelete={handleDelete} />
          </>}
        />)}
        {!showDefault && !filtered.length && <li className='p-2 text-center twy-text-base'>No versions found.</li>}
      </ul>
    </div>
    <div className={cn('overflow-hidden transition-[width] duration-300 ease-in-out', creating ? 'w-76' : 'w-0', formVisible ? 'self-stretch' : 'h-0')}>
      <div className={cn('flex h-full w-76 transition-transform duration-300 ease-in-out', creating ? 'translate-x-0' : 'translate-x-full')}>
        <Separator orientation='vertical' className='mx-3 my-0 h-auto self-stretch' />
        <div className='flex-1 min-w-0'>
          <p className='font-bold'>Create new version</p>
          <Separator className='m-0 my-2' />
          <Form key={formKey} data={data} version={current} versions={versions} onSubmit={handleCreate} />
        </div>
      </div>
    </div>
  </>
}

function Item({ label, hint, isCurrent, actions }) {
  return <li className={cn(
    'group flex items-center justify-between gap-2 rounded-sm px-3 py-1.5 cursor-default transition-colors hover:twy-subtle-accent hover:twy-text-accent',
    isCurrent ? 'twy-text-accent font-semibold' : 'twy-text-base',
  )}>
    <span className='flex items-center gap-2 min-w-0'>
      <span className='truncate'>{label}</span>
      {hint && <Badge variant='outline' size='sm' className='self-center font-normal truncate'>{hint}</Badge>}
    </span>
    {!isCurrent && <span className='flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity'>
      {actions}
    </span>}
  </li>
}

const ACTION_COLORS = {
  accent: 'hover:twy-text-accent',
  danger: 'hover:text-red-500 dark:hover:text-red-300',
}

function Action({ tooltip, icon: Icon, onClick, disabled, color = 'accent' }) {
  const className = cn(
    'flex items-center twy-text-base-neutral transition-colors',
    disabled ? 'opacity-50 cursor-not-allowed' : cn('cursor-pointer', ACTION_COLORS[color]),
  )

  return <DefaultTooltip content={tooltip} side='top' triggerProps={{ className, onClick: disabled ? undefined : onClick }}>
    <Icon size={14} />
  </DefaultTooltip>
}

function DeleteAction({ version, onDelete }) {
  const [open, setOpen] = useState(false)

  const handleDelete = async () => {
    await onDelete(version)
    setOpen(false)
  }

  return <ConfirmationPopover
    title='Delete Version'
    className='flex items-center'
    open={open}
    onOpenChange={setOpen}
    target={<Action tooltip='Delete' icon={Trash} color='danger' />}
  >
    <div className='mb-2 font-bold'>Are you sure you want to delete this version?</div>
    <Button color='danger' className='mt-2 w-full flex justify-center' onClick={handleDelete}>
      Yes, I am sure
    </Button>
  </ConfirmationPopover>
}
